/** Requêtes et mutations partagées, via React Query. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { amazonTemplate, meta, profiles, somReference } from '@/api/client'
import type { Profile, RuleSet, SomReferenceSettings, SomSummary } from '@/api/types'

export const keys = {
  meta: ['meta'] as const,
  template: ['amazon-template'] as const,
  somReference: ['som-reference'] as const,
  profiles: ['profiles'] as const,
  profile: (id: string) => ['profiles', id] as const,
  versions: (id: string) => ['profiles', id, 'versions'] as const,
}

export function useMeta() {
  // Le référentiel (champs, valeurs livrées, modèle Amazon) ne bouge qu'au
  // déploiement ou au téléversement d'un modèle : inutile de le redemander.
  return useQuery({ queryKey: keys.meta, queryFn: meta.get, staleTime: 5 * 60 * 1000 })
}

export function useProfiles() {
  return useQuery({ queryKey: keys.profiles, queryFn: profiles.list })
}

export function useProfile(id: string | undefined) {
  return useQuery({
    queryKey: keys.profile(id ?? ''),
    queryFn: () => profiles.get(id as string),
    enabled: Boolean(id),
  })
}

export function useProfileVersions(id: string | undefined) {
  return useQuery({
    queryKey: keys.versions(id ?? ''),
    queryFn: () => profiles.versions(id as string),
    enabled: Boolean(id),
  })
}

/** Rafraîchit la liste et le profil touché après une écriture. */
function useProfileInvalidation() {
  const client = useQueryClient()
  return (profile?: Profile) => {
    void client.invalidateQueries({ queryKey: keys.profiles })
    if (profile) client.setQueryData(keys.profile(profile.id), profile)
    if (profile) void client.invalidateQueries({ queryKey: keys.versions(profile.id) })
  }
}

export function useSaveProfile() {
  const refresh = useProfileInvalidation()
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; name?: string; rules?: RuleSet; note?: string }) =>
      profiles.update(id, payload),
    onSuccess: refresh,
  })
}

export function useCreateProfile() {
  const refresh = useProfileInvalidation()
  return useMutation({
    mutationFn: ({ name, rules }: { name: string; rules?: RuleSet }) => profiles.create(name, rules),
    onSuccess: refresh,
  })
}

export function useDeleteProfile() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => profiles.remove(id),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.profiles }),
  })
}

export function useSetDefaultProfile() {
  const refresh = useProfileInvalidation()
  return useMutation({ mutationFn: (id: string) => profiles.setDefault(id), onSuccess: refresh })
}

export function useRestoreVersion() {
  const refresh = useProfileInvalidation()
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => profiles.restore(id, version),
    onSuccess: refresh,
  })
}

export function useUploadTemplate() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => amazonTemplate.upload(file),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.meta })
      void client.invalidateQueries({ queryKey: keys.template })
    },
  })
}

export function useResetTemplate() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => amazonTemplate.reset(),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.meta })
      void client.invalidateQueries({ queryKey: keys.template })
    },
  })
}

export function useSomReference() {
  return useQuery({ queryKey: keys.somReference, queryFn: somReference.get })
}

/** Dépose, corrige ou retire le catalogue SOM ; la réponse remplace le cache. */
function useSomMutation<T>(mutationFn: (input: T) => Promise<SomSummary | null | void>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (data) => client.setQueryData(keys.somReference, data ?? null),
  })
}

export function useUploadSomReference() {
  return useSomMutation((file: File) => somReference.upload(file))
}

export function useUpdateSomReference() {
  return useSomMutation((settings: SomReferenceSettings) => somReference.update(settings))
}

export function useRemoveSomReference() {
  return useSomMutation<void>(() => somReference.remove())
}
